'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { parseDate, today } from '@internationalized/date'
import { Input, Select, SelectItem, SelectSection, Textarea } from '@nextui-org/react'
import { I18nProvider, useLocale as useAriaLocale } from '@react-aria/i18n'
import {
  Calendar,
  Clock,
  Hourglass,
  Loader2,
  Mail,
  Store,
  User,
  Users
} from 'lucide-react'
import { useLocale as useSiteLocale } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { PhoneInput } from '@/components/forms/phone-input'
import { DatePicker } from '@/components/ui/datepicker'
import { env } from '@/constants/configs'
import { getGiftCapStatus } from '@/libs/actions/redeem.action'
import { devWarn } from '@/libs/devLog'
import { locales } from '@/libs/intl'

import {
  GUEST_OPTIONS,
  HOURS_OPTIONS,
  makeBookingSchema,
  type AvailabilityInput,
  type BookingFormValues
} from '../hooks/redeem.schema'
import { useNearestBranch } from '../hooks/useNearestBranch'
import { useRedeemT } from '../i18n/useRedeemT'
import type {
  IBranchOption,
  IGiftDetail,
  IRedeemErrorVariant,
  IRedeemFilter,
  IRedeemQueryPrefill,
  IRedeemRecommend
} from '../types/schema'
import { filterTimeSlots } from '../utils/timeSlots'
import ErrorState from './ErrorState'
import RecommendPanel from './RecommendPanel'
import StickySummaryBar from './StickySummaryBar'
import { CARD, CTA_PRIMARY, SECTION_NUMBER } from './styles'

/** Icons the legacy page had (`icon=` per `<input-element>`). Muted, so label and value lead. */
const FIELD_ICON = 'pointer-events-none h-4 w-4 shrink-0 text-theme/60'

const DATE_TRIGGER_PROPS = {
  variant: 'bordered',
  size: undefined,
  radius: undefined,
  isRequired: false,
  classNames: { input: 'cursor-pointer', inputWrapper: 'cursor-pointer' },
  startContent: <Calendar className={FIELD_ICON} />
} as const

/** `YYYY-MM-DD` -> CalendarDate. A malformed value must not break the picker; Zod still rejects it. */
const toCalendarDate = (value?: string) => {
  if (!value) return null

  try {
    return parseDate(value.slice(0, 10))
  } catch {
    return null
  }
}

interface Props {
  blocked: boolean   /** Maintenance */
  gift: IGiftDetail
  filter: IRedeemFilter
  prefill: IRedeemQueryPrefill | null
  recommend: IRedeemRecommend | null
  error: IRedeemErrorVariant | null
  isBooking: boolean
  isCheckingAvailability: boolean
  onSubmitBooking: (values: BookingFormValues, cityId: number) => Promise<boolean>
  /** Resolves false when the slot is taken; the panel below then shows the alternatives. */
  onCheckAvailability: (values: AvailabilityInput) => Promise<boolean>
  onRecommendTaken: () => void
  onStartOver: () => void
}

export default function BookingSection({
  blocked,
  gift,
  filter,
  prefill,
  recommend,
  error,
  isBooking,
  isCheckingAvailability,
  onSubmitBooking,
  onCheckAvailability,
  onRecommendTaken,
  onStartOver
}: Props) {
  const t = useRedeemT()
  const { locale } = useAriaLocale()

  const siteLocale = useSiteLocale()
  const pickerLocale = useMemo(
    () => locales.find((r) => r.key === siteLocale)?.locale,
    [siteLocale]
  )

  // ── Form ─────────────────────────────────────────────────────────────────
  const schema = useMemo(() => makeBookingSchema(gift.expired), [gift.expired])

  // Dismissing the bar is safe: the same submit lives at the foot of the form.
  const [summaryHidden, setSummaryHidden] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, submitCount }
  } = useForm<BookingFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      branch: prefill?.branch ? Number(prefill.branch) : undefined,
      date: prefill?.date ?? '',
      time: prefill?.time ?? '',
      hours: undefined,
      guest: prefill?.guest ? Number(prefill.guest) : 1,
      yourName: '',
      yourEmail: '',
      yourPhone: '',
      remark: ''
    } as Partial<BookingFormValues> as BookingFormValues
  })

  const branchId = useWatch({ control, name: 'branch' })
  const date = useWatch({ control, name: 'date' })
  const time = useWatch({ control, name: 'time' })
  const hours = useWatch({ control, name: 'hours' })
  const guest = useWatch({ control, name: 'guest' })
  const yourPhone = useWatch({ control, name: 'yourPhone' })

  // ── Derived from the five booking fields ─────────────────────────────────
  const selectedBranch = useMemo(
    () => filter.branch.find((b) => b.branch_id === Number(branchId)) ?? null,
    [filter.branch, branchId]
  )

  const branchesByCity = useMemo(() => {
    const groups = new Map<number, { cityName: string; branches: IBranchOption[] }>()

    for (const branch of filter.branch) {
      const group = groups.get(branch.city_id)

      if (group) group.branches.push(branch)
      else groups.set(branch.city_id, { cityName: branch.city_name, branches: [branch] })
    }

    return [...groups].map(([cityId, group]) => ({ cityId, ...group }))
  }, [filter.branch])

  // A server-recommended slot, kept selectable past our own window cut — that cut approximates
  // (browser clock, generic branch hours) and must not overrule the server.
  // Branch+date keyed, so it dies with them.
  const [pinnedSlot, setPinnedSlot] = useState<{
    branch: number
    date: string
    time: string
  } | null>(null)

  const availableSlots = useMemo(() => {
    const slots =
      selectedBranch && date
        ? filterTimeSlots({ slots: filter.time, branch: selectedBranch, date })
        : filter.time

    if (!pinnedSlot || pinnedSlot.branch !== Number(branchId) || pinnedSlot.date !== date) {
      return slots
    }
    if (slots.some((slot) => slot.time_start === pinnedSlot.time)) return slots

    const pinned = filter.time.find((slot) => slot.time_start === pinnedSlot.time)

    return pinned ? [...slots, pinned].sort((a, b) => a.time_id - b.time_id) : slots
  }, [filter.time, selectedBranch, branchId, date, pinnedSlot])

  // ── Branch pre-select ────────────────────────────────────────────────────
  const pickNearestBranch = useCallback(
    (nearestId: number) => setValue('branch', nearestId, { shouldValidate: true }),
    [setValue]
  )

  // `branchId` already holds any `?branch=` prefill, so empty is the only case worth guessing at.
  useNearestBranch({
    branches: filter.branch,
    enabled: !branchId,
    onPick: pickNearestBranch
  })

  // ── Branches at their daily eGift limit ──────────────────────────────────
  // The cap is per branch-day, so the date alone decides it. Asked here rather than left to the
  // per-slot check, which cannot speak until a branch AND a time are chosen — by then the visitor
  // has already picked the branch we would have told them to avoid.
  const [fullBranchIds, setFullBranchIds] = useState<ReadonlySet<number>>(new Set())

  /** Identifies the newest lookup, so a slow earlier one cannot overwrite its answer. */
  const capRequestId = useRef(0)

  useEffect(() => {
    const requestId = ++capRequestId.current

    if (!date) {
      setFullBranchIds(new Set())
      return
    }

    void getGiftCapStatus(
      date,
      filter.branch.map((branch) => branch.branch_id)
    ).then((response) => {
      if (requestId !== capRequestId.current) return

      // No answer is no opinion: every branch stays selectable and the pre-flight check still
      // catches a full one before the booking goes out. `date` is re-checked because the action
      // echoes it back, which is the only way to spot an answer for a date already left behind.
      if (!response || response.date !== date) {
        setFullBranchIds(new Set())
        return
      }

      setFullBranchIds(
        new Set(response.branches.filter((row) => row.is_full).map((row) => row.branch_id))
      )
    })
  }, [date, filter.branch])

  // A branch already chosen is deliberately NOT cleared when a date turns it full: clearing it
  // would also stop the availability check from ever running, and that check is what produces the
  // suggestion panel — the alternative branches and their times. The visitor keeps their choice,
  // sees it marked full in the list, and gets somewhere to go once the remaining fields are set.

  // Changing branch or date can invalidate the chosen slot. Drop it rather than submit a reject.
  useEffect(() => {
    if (!time) return
    if (availableSlots.some((slot) => slot.time_start === time)) return

    setValue('time', '', { shouldValidate: false })
  }, [availableSlots, time, setValue])

  // ── Availability & suggestions ───────────────────────────────────────────
  const handleRecommendPick = useCallback(
    (pickedBranch: number, pickedTime: string) => {
      setPinnedSlot({ branch: pickedBranch, date, time: pickedTime })
      setValue('branch', pickedBranch, { shouldValidate: true })
      setValue('time', pickedTime, { shouldValidate: true })
      onRecommendTaken()
    },
    [setValue, onRecommendTaken, date]
  )

  /**
   * Every chip must be clickable, so drop what the dropdowns lack. Branch: Laravel recommends
   * zone-wide (`getBranchInfo("zone_id")`), `filter.branch` is product-limited (`.., pd_id`).
   * Time: `filter.time` only — `filterTimeSlots` re-derives the server's cuts and emptied the panel.
   */
  const recommendToShow = useMemo(() => {
    if (!recommend) return null

    const options = recommend.options
      .filter((option) => filter.branch.some((b) => b.branch_id === option.branchId))
      .map((option) => ({
        ...option,
        times: option.times.filter((slot) => filter.time.some((s) => s.time_start === slot))
      }))
      .filter((option) => option.times.length > 0)

    if (options.length < recommend.options.length) {
      devWarn('[redeem] suggestion dropped — not selectable in this form', recommend.options)
    }

    return { ...recommend, options }
  }, [recommend, filter])

  /**
   * Any "no" from the pre-flight blocks the submit — gift cap, no rooms, outside hours, too soon.
   * Every one of them means this exact slot cannot be booked, so there is nothing to gain by
   * letting the visitor spend a click finding out.
   *
   * Reads `recommend`, NOT `recommendToShow`: the latter drops options this form cannot offer, and
   * an empty list there still means the booking was refused.
   *
   * `isCheckingAvailability` is in here too, so the gap between a field changing and the new
   * answer arriving stays closed rather than briefly re-enabling on a stale pass.
   */
  const unbookable = recommend !== null || isCheckingAvailability

  // Fires once all five booking fields are set, so the answer arrives while the user is still on
  // their details rather than after Confirm. Debounced: branch/date changes rewrite the slot list.
  useEffect(() => {
    if (!branchId || !date || !time || !hours || !guest) return

    const timer = setTimeout(() => {
      void onCheckAvailability({ branch: Number(branchId), date, time, hours, guest })
    }, 400)

    return () => clearTimeout(timer)
  }, [branchId, date, time, hours, guest, onCheckAvailability])

  useEffect(() => {
    if (!submitCount) return

    document
      .querySelector('.redeem-booking [data-invalid="true"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [submitCount])

  // ── Submit ───────────────────────────────────────────────────────────────
  const submit = handleSubmit(async (values) => {
    // `city` is derived from the branch, so it is never a form field.
    const cityId = selectedBranch?.city_id ?? 0
    await onSubmitBooking(values, cityId)
  })

  // ── Render ───────────────────────────────────────────────────────────────
  // Two numbered sections, one <form>, deliberately not split: both read the same `useForm`, so
  // splitting means prop-drilling register/setValue/errors/... or a FormProvider. More indirection.
  return (
    <form onSubmit={submit} noValidate>
      <section className='container mx-auto max-w-4xl px-4 py-6'>
        <div className={`${CARD} redeem-booking p-6 md:p-8`}>
          {/* ── Section 2 · when and where ── */}
          <h2 className='text-xl font-bold text-gray-900'>
            <span className={SECTION_NUMBER}>2</span>
            {t('TITLE_BOOKING_INFO')}
          </h2>
          <p className='mb-6 mt-1 text-sm text-gray-500'>{t('DESCRIPTION_BOOKING_INFO')}</p>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <Select
              label={t('LABEL_INPUT_LOCATION')}
              variant='bordered'
              placeholder={t('LABEL_INPUT_SELECT_PLACEHOLDER')}
              selectedKeys={branchId ? [String(branchId)] : []}
              onChange={(e) => setValue('branch', Number(e.target.value), { shouldValidate: true })}
              startContent={<Store className={FIELD_ICON} />}
              // Full branches stay listed rather than disappearing: a location the visitor is
              // looking for that is simply absent reads as a broken form.
              // The current selection is never disabled — it was legal when it was picked, and
              // greying out the value the field is showing reads as a bug. It keeps its "no eGift
              // slots" label, and the availability check below turns it into a suggestion.
              disabledKeys={[...fullBranchIds]
                .filter((id) => id !== Number(branchId))
                .map(String)}
              isInvalid={Boolean(errors.branch)}
              errorMessage={errors.branch && t('ERROR_INPUT_REQUIRED')}>
              {branchesByCity.map((city) => (
                <SelectSection
                  key={`city-${city.cityId}`}
                  title={city.cityName}
                  classNames={{ heading: 'text-sm font-bold text-theme' }}>
                  {city.branches.map((branch) => (
                    // `textValue` because the full ones render an element, not a string, and
                    // type-ahead and screen readers both read this instead.
                    <SelectItem
                      key={String(branch.branch_id)}
                      textValue={branch.branch_long_name}>
                      {fullBranchIds.has(branch.branch_id) ? (
                        <span className='flex items-center justify-between gap-2'>
                          <span>{branch.branch_long_name}</span>
                          <span className='shrink-0 text-xs font-bold uppercase tracking-wide text-amber-700'>
                            {t('LABEL_BRANCH_GIFT_FULL')}
                          </span>
                        </span>
                      ) : (
                        branch.branch_long_name
                      )}
                    </SelectItem>
                  ))}
                </SelectSection>
              ))}
            </Select>

            <I18nProvider locale={pickerLocale}>
              <DatePicker
                lang={pickerLocale}
                label={t('LABEL_INPUT_DATE')}
                value={date ?? ''}
                minDate={today(env.TZ)}
                maxDate={toCalendarDate(gift.expired) ?? undefined}
                inputProps={{
                  ...DATE_TRIGGER_PROPS,
                  placeholder: t('LABEL_INPUT_SELECT_PLACEHOLDER')
                }}
                onChange={(value) =>
                  setValue('date', value ? value.toString() : '', { shouldValidate: true })
                }
                errorMessage={
                  errors.date
                    ? t(
                      errors.date.message === 'DATE_AFTER_EXPIRY'
                        ? 'ERROR_INPUT_DATE_AFTER_EXPIRY'
                        : errors.date.message === 'DATE_PAST'
                          ? 'ERROR_INPUT_DATE_PAST'
                          : 'ERROR_INPUT_DATE_INVALID'
                    )
                    : undefined
                }
              />
            </I18nProvider>

            <Select
              label={t('LABEL_INPUT_TIME')}
              variant='bordered'
              placeholder={t('LABEL_INPUT_SELECT_PLACEHOLDER')}
              selectedKeys={time ? [time] : []}
              onChange={(e) => setValue('time', e.target.value, { shouldValidate: true })}
              startContent={<Clock className={FIELD_ICON} />}
              isInvalid={Boolean(errors.time)}
              errorMessage={errors.time && t('ERROR_INPUT_REQUIRED')}
              description={
                !selectedBranch || !date
                  ? t('NOTICE_PICK_BRANCH_FIRST')
                  : availableSlots.length === 0
                    ? t('NOTICE_NO_TIME_AVAILABLE')
                    : undefined
              }>
              {availableSlots.map((slot) => (
                <SelectItem key={slot.time_start}>{slot.time_start}</SelectItem>
              ))}
            </Select>

            <Select
              label={t('LABEL_INPUT_TREATMENT_HOURS')}
              variant='bordered'
              placeholder={t('LABEL_INPUT_SELECT_PLACEHOLDER')}
              selectedKeys={hours ? [hours] : []}
              onChange={(e) =>
                setValue('hours', e.target.value as BookingFormValues['hours'], {
                  shouldValidate: true
                })
              }
              startContent={<Hourglass className={FIELD_ICON} />}
              isInvalid={Boolean(errors.hours)}
              errorMessage={errors.hours && t('ERROR_INPUT_REQUIRED')}>
              {HOURS_OPTIONS.map((option) => (
                <SelectItem key={option}>{option}</SelectItem>
              ))}
            </Select>

            <Select
              label={t('LABEL_INPUT_GUEST')}
              variant='bordered'
              selectedKeys={guest ? [String(guest)] : []}
              onChange={(e) =>
                setValue('guest', Number(e.target.value) as 1 | 2, { shouldValidate: true })
              }
              startContent={<Users className={FIELD_ICON} />}
              isInvalid={Boolean(errors.guest)}
              errorMessage={errors.guest && t('ERROR_INPUT_REQUIRED')}>
              {GUEST_OPTIONS.map((option) => (
                <SelectItem key={String(option)}>
                  {t('LABEL_GUEST_COUNT', { count: option })}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Both states of the availability answer sit here, directly under the five fields that
              produced it — a refusal about the spa, the date or the time belongs beside those
              fields, not below a name and email the visitor has yet to fill in. */}
          {isCheckingAvailability && (
            <p role='status' className='mt-6 flex items-center gap-2 text-sm text-gray-500'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
              {t('NOTICE_CHECKING_AVAILABILITY')}
            </p>
          )}

          {recommendToShow && !isCheckingAvailability && (
            <RecommendPanel recommend={recommendToShow} onPick={handleRecommendPick} />
          )}

          {/* ── Section 3 · customer details ── */}
          <h2 className='mb-1 mt-10 text-xl font-bold text-gray-900'>
            <span className={SECTION_NUMBER}>3</span>
            {t('TITLE_CUSTOMER_INFO')}
          </h2>
          <p className='mb-6 text-sm text-gray-500'>{t('DESCRIPTION_CUSTOMER_INFO_EMAIL')}</p>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <Input
              {...register('yourName')}
              label={t('LABEL_INPUT_YOUR_NAME')}
              variant='bordered'
              startContent={<User className={FIELD_ICON} />}
              isInvalid={Boolean(errors.yourName)}
              // The schema emits TOO_LONG here too, which must not read as "required".
              errorMessage={
                errors.yourName &&
                t(
                  errors.yourName.message === 'TOO_LONG'
                    ? 'ERROR_INPUT_TOO_LONG'
                    : 'ERROR_INPUT_REQUIRED'
                )
              }
            />
            <Input
              {...register('yourEmail')}
              label={t('LABEL_INPUT_YOUR_EMAIL')}
              type='email'
              variant='bordered'
              startContent={<Mail className={FIELD_ICON} />}
              isInvalid={Boolean(errors.yourEmail)}
              errorMessage={
                errors.yourEmail &&
                t(
                  errors.yourEmail.message === 'EMAIL_FORMAT'
                    ? 'ERROR_INPUT_EMAIL_FORMAT'
                    : 'ERROR_INPUT_EMAIL_REQUIRED'
                )
              }
            />

            <div className='md:col-span-2' data-invalid={errors.yourPhone ? 'true' : undefined}>
              <label
                htmlFor='redeem-phone'
                className='redeem-field-label mb-1.5 block text-xs font-medium text-gray-600'>
                {t('LABEL_INPUT_MOBILE')}
              </label>
              <PhoneInput
                id='redeem-phone'
                name='yourPhone'
                defaultCountry='TH'
                countryOptionsOrder={['TH', 'US', 'CN', 'JP', 'KR']}
                value={yourPhone}
                className='redeem-phone'
                onChange={(value) => setValue('yourPhone', value, { shouldValidate: true })}
              />
              {errors.yourPhone && (
                <p className='mt-1.5 text-xs text-danger'>
                  {t(
                    errors.yourPhone.message === 'PHONE_FORMAT'
                      ? 'ERROR_INPUT_PHONE_FORMAT'
                      : 'ERROR_INPUT_REQUIRED'
                  )}
                </p>
              )}
            </div>

            <Textarea
              {...register('remark')}
              label={t('LABEL_INPUT_REMARK_OPTIONAL')}
              variant='bordered'
              minRows={2}
              className='md:col-span-2'
            />
          </div>

          {error && <ErrorState variant={error} giftNumber={gift.gift_number} prefill={prefill} />}

          <div className='mt-8 flex flex-col items-center gap-4 border-t border-gray-100 pt-6'>
            <button
              type='submit'
              disabled={blocked || isBooking || unbookable}
              className={`${CTA_PRIMARY} w-full md:w-auto md:min-w-64 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:scale-100 disabled:hover:bg-gray-300 disabled:hover:shadow-none`}>
              {isBooking ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' /> {t('BUTTON_BOOKING_LOADING')}
                </>
              ) : (
                t('BUTTON_SUBMIT')
              )}
            </button>

            <button
              type='button'
              onClick={onStartOver}
              className='text-sm font-medium text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900'>
              {t('BUTTON_CHANGE_GIFT')}
            </button>
          </div>
        </div>
      </section>

      {!summaryHidden && (
        <StickySummaryBar
          branchName={selectedBranch?.branch_long_name}
          date={date}
          time={time}
          hours={hours}
          guest={guest}
          blocked={blocked}
          isBooking={isBooking}
          unbookable={unbookable}
          locale={locale}
          onClose={() => setSummaryHidden(true)}
        />
      )}
    </form>
  )
}
